import { ValueOf } from '../utilities/types';

export const errorCodes = {
  ALREADY_ASSIGNED: 'ALREADY_ASSIGNED',
  ALREADY_ESCALATED: 'ALREADY_ESCALATED',
  CREDENTIALS_LOGIN_NOT_CONFIGURED: 'CREDENTIALS_LOGIN_NOT_CONFIGURED',
  INCOMPATIBLE_IDENTIFIER_FIELDS: 'INCOMPATIBLE_IDENTIFIER_FIELDS',
  MFA_ALREADY_ENABLED: 'MFA_ALREADY_ENABLED',
  MFA_CHALLENGE_TOKEN_ALREADY_USED: 'MFA_CHALLENGE_TOKEN_ALREADY_USED',
  MFA_CODE_REQUIRED: 'MFA_CODE_REQUIRED',
  MFA_INVALID_CHALLENGE_TOKEN: 'MFA_INVALID_CHALLENGE_TOKEN',
  MFA_INVALID_PENDING_ENROLLMENT_TOKEN: 'MFA_INVALID_PENDING_ENROLLMENT_TOKEN',
  MFA_INVALID_RECOVERY_CODE: 'MFA_INVALID_RECOVERY_CODE',
  MFA_INVALID_VERIFICATION_CODE: 'MFA_INVALID_VERIFICATION_CODE',
  MFA_LOCKED: 'MFA_LOCKED',
  MFA_NOT_ENABLED: 'MFA_NOT_ENABLED',
  MFA_NO_PENDING_ENROLLMENT: 'MFA_NO_PENDING_ENROLLMENT',
  MFA_RECOVERY_CODE_ALREADY_USED: 'MFA_RECOVERY_CODE_ALREADY_USED',
  MFA_REQUIRED_ON_ACCOUNT: 'MFA_REQUIRED_ON_ACCOUNT',
  MFA_VERIFICATION_CODE_ALREADY_USED: 'MFA_VERIFICATION_CODE_ALREADY_USED',
  MANUAL_PING_SCORE_NOT_ALLOWED: 'MANUAL_PING_SCORE_NOT_ALLOWED',
  MISSING_DATA_PROVIDER: 'MISSING_DATA_PROVIDER',
  MISSING_DATA_CREDENTIALS: 'MISSING_DATA_CREDENTIALS',
  MISSING_REQUIRED_DATA_FOR_PROVIDER: 'MISSING_REQUIRED_DATA_FOR_PROVIDER',
  NON_UNIQUE_EMAIL: 'NON_UNIQUE_EMAIL',
  NON_UNIQUE_NAME: 'NON_UNIQUE_NAME',
  PASSWORD_CONTAINS_EMOJI: 'PASSWORD_CONTAINS_EMOJI',
  PASSWORD_MISSING_CHARACTER_CLASSES: 'PASSWORD_MISSING_CHARACTER_CLASSES',
  PASSWORD_POLICY_VIOLATION: 'PASSWORD_POLICY_VIOLATION',
  PASSWORD_TOO_LONG: 'PASSWORD_TOO_LONG',
  PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',
  PERMISSION_LOCKOUT: 'PERMISSION_LOCKOUT',
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
  PERMISSION_ASSIGNED: 'PERMISSION_ASSIGNED',
  PUBLISH_IN_PROGRESS: 'PUBLISH_IN_PROGRESS',
  RESET_PASSWORD_INVALID_TOKEN: 'RESET_PASSWORD_INVALID_TOKEN',
  PENDING_REVIEW_EXISTS: 'PENDING_REVIEW_EXISTS',
  SSO_RELAY_STATE_NOT_FOUND: 'SSO_RELAY_STATE_NOT_FOUND',
  SSO_ALIAS_NOT_FOUND: 'SSO_ALIAS_NOT_FOUND',
  SSO_NOT_CONFIGURED: 'SSO_NOT_CONFIGURED',
  SSO_INVALID_SIGNATURE: 'SSO_INVALID_SIGNATURE',
  SSO_AUTH_TIMEOUT: 'SSO_AUTH_TIMEOUT',
  SSO_INVALID_RESPONSE: 'SSO_INVALID_RESPONSE',
  SSO_INVALID_IDP: 'SSO_INVALID_IDP',
  SSO_AUTH_FAILED: 'SSO_AUTH_FAILED',
  SSO_NO_ACCESS: 'SSO_NO_ACCESS',
  SSO_INVALID_TOKEN: 'SSO_INVALID_TOKEN',
  TOO_MANY_RELATED_ENTITIES: 'TOO_MANY_RELATED_ENTITIES',
  TOO_MANY_PINGS_ON_CASE: 'TOO_MANY_PINGS_ON_CASE',
  TOO_MANY_ENTITIES_ON_CASE: 'TOO_MANY_ENTITIES_ON_CASE',
  UNSUPPORTED_IMAGE_TYPE: 'UNSUPPORTED_IMAGE_TYPE',
} as const;

export type ErrorCodes = ValueOf<typeof errorCodes>;

const errorMessages: Record<ErrorCodes, string> = {
  [errorCodes.ALREADY_ASSIGNED]: 'This case is currently assigned to someone else',
  [errorCodes.ALREADY_ESCALATED]: 'This case is currently escalated to someone else',
  [errorCodes.CREDENTIALS_LOGIN_NOT_CONFIGURED]: 'This account signs in through SSO, so the setting does not apply',
  [errorCodes.INCOMPATIBLE_IDENTIFIER_FIELDS]: 'The identifier fields provided are incompatible with previous entities.',
  [errorCodes.MFA_ALREADY_ENABLED]: 'Multi-factor authentication is already enabled',
  [errorCodes.MFA_CHALLENGE_TOKEN_ALREADY_USED]: 'This sign in has already been completed. Please sign in again',
  [errorCodes.MFA_CODE_REQUIRED]: 'A verification code or a recovery code is required',
  [errorCodes.MFA_INVALID_CHALLENGE_TOKEN]: 'Your sign in expired. Please sign in again',
  [errorCodes.MFA_INVALID_PENDING_ENROLLMENT_TOKEN]: 'This setup is no longer valid. Please start it again',
  [errorCodes.MFA_INVALID_RECOVERY_CODE]: 'Invalid recovery code',
  [errorCodes.MFA_INVALID_VERIFICATION_CODE]: 'Invalid verification code',
  [errorCodes.MFA_LOCKED]: 'Multi-factor authentication is locked after too many failed attempts',
  [errorCodes.MFA_NOT_ENABLED]: 'Multi-factor authentication is not enabled',
  [errorCodes.MFA_NO_PENDING_ENROLLMENT]: 'There is no setup in progress. Please start it again',
  [errorCodes.MFA_RECOVERY_CODE_ALREADY_USED]: 'Recovery code already used',
  [errorCodes.MFA_REQUIRED_ON_ACCOUNT]: 'Multi-factor authentication is required on this account. Replace your authenticator device instead of turning it off',
  [errorCodes.MFA_VERIFICATION_CODE_ALREADY_USED]: 'Verification code already used',
  [errorCodes.MANUAL_PING_SCORE_NOT_ALLOWED]: 'A manual ping can no longer carry a score. Raise the risk by assigning a group with a score instead',
  [errorCodes.MISSING_DATA_PROVIDER]: 'You do not have any data provider setup for this kind of information. Please contact sales if you would like to set this up',
  [errorCodes.MISSING_DATA_CREDENTIALS]: 'You do not have any credentials setup for this kind of information. Please contact sales if you would like to set this up',
  [errorCodes.MISSING_REQUIRED_DATA_FOR_PROVIDER]: 'You are missing required data on this entity to use this provider',
  [errorCodes.NON_UNIQUE_EMAIL]: 'The email is already used by another member',
  [errorCodes.NON_UNIQUE_NAME]: 'The name already exist',
  [errorCodes.PASSWORD_CONTAINS_EMOJI]: 'The password contains emoji',
  [errorCodes.PASSWORD_MISSING_CHARACTER_CLASSES]: 'The password does not mix enough character types',
  [errorCodes.PASSWORD_POLICY_VIOLATION]: 'The password does not meet the Password Policy',
  [errorCodes.PASSWORD_TOO_LONG]: 'The password is too long',
  [errorCodes.PASSWORD_TOO_SHORT]: 'The password is too short',
  [errorCodes.PERMISSION_LOCKOUT]: 'This action is not permitted on yourself as it would lock you out of permissions management. Ensure another member is allowed to manage permissions and ask them to do that update for your member account',
  [errorCodes.PROVIDER_UNAVAILABLE]: 'The data provider is experiencing issues at the moment. Please try again later. If the problem persists, reach out to our support.',
  [errorCodes.PERMISSION_ASSIGNED]: 'This action is not permitted as the permission is assigned to a member or API key. Please remove the permission first',
  [errorCodes.RESET_PASSWORD_INVALID_TOKEN]: 'Could not set new password. The set password link is most likely expired or invalid.',
  [errorCodes.PUBLISH_IN_PROGRESS]: 'A publish operation is currently in progress. Please wait until it completes before attempting this action.',
  [errorCodes.PENDING_REVIEW_EXISTS]: 'There is already a pending review for this action. Please wait until it is resolved before attempting this action again.',
  [errorCodes.SSO_RELAY_STATE_NOT_FOUND]: 'Could not recover session',
  [errorCodes.SSO_ALIAS_NOT_FOUND]: 'Account alias not found',
  [errorCodes.SSO_NOT_CONFIGURED]: 'SSO login method not configured for this account',
  [errorCodes.SSO_INVALID_SIGNATURE]: 'Invalid signature',
  [errorCodes.SSO_AUTH_TIMEOUT]: 'Authentication timed out',
  [errorCodes.SSO_INVALID_RESPONSE]: 'Invalid SAML response',
  [errorCodes.SSO_INVALID_IDP]: 'Unexpected SAML identity provider',
  [errorCodes.SSO_AUTH_FAILED]: 'Authentication failed',
  [errorCodes.SSO_NO_ACCESS]: 'You do not have access to this application',
  [errorCodes.SSO_INVALID_TOKEN]: 'Invalid token',
  [errorCodes.TOO_MANY_RELATED_ENTITIES]: 'There are too many related entities to be able to retrieve the information. Please try to narrow it down to exclude the related entities.',
  [errorCodes.TOO_MANY_PINGS_ON_CASE]: 'There are too many pings on this case. Please try to narrow it down to exclude some pings or create a new case with the remaining pings.',
  [errorCodes.TOO_MANY_ENTITIES_ON_CASE]: 'There are too many entities on this case. Please try to narrow it down to exclude some entities or create a new case with the remaining entities.',
  [errorCodes.UNSUPPORTED_IMAGE_TYPE]: 'The image must be a PNG, JPEG or WebP file',
};

export default errorMessages;
